import Typography from "antd/lib/typography";
import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { useCurrentBrandQuery } from "@sol/sdk/current-brand";

import { SkipLinks } from "../SkipLinks";
import LoginLayoutHero from "./LoginLayoutHero";

const ATLAS_LAUREATE_BRAND_TITLE = "simplon";

type LoginLayoutProps = {
    title: string;
    className?: string;
};

const LoginLayout: FC<LoginLayoutProps> = ({ children, title, className }) => {
    const [t] = useTranslation();

    const { data: currentBrand } = useCurrentBrandQuery({}, { suspense: true });
    const brand = currentBrand?.brand;
    const showAtlasLogo = brand?.title.toLowerCase() === ATLAS_LAUREATE_BRAND_TITLE;

    return (
        <div
            className={clsx(
                "h-dvh min-w-ant-screen-xs overflow-auto overscroll-none bg-ant-container bg-[length:20%] sm:bg-ant-fill-quaternary sm:bg-pattern",
                className,
            )}
        >
            <SkipLinks />
            <main
                role="main"
                id="main-content"
                className={clsx(
                    // START CONTAINER
                    "mx-auto w-full",
                    "sm:max-w-ant-screen-sm",
                    "md:max-w-ant-screen-md",
                    "lg:max-w-ant-screen-lg",
                    "xl:max-w-ant-screen-xl",
                    // END CONTAINER
                    "flex min-h-full flex-col items-center justify-center gap-ant-md xl:flex-row xl:items-start xl:justify-around",
                )}
            >
                <div
                    className={clsx(
                        "w-full max-w-[450px] p-ant-md pb-0 xl:sticky xl:top-0 xl:min-h-[100cqh] xl:pb-ant-xl",
                        "flex items-center justify-center",
                    )}
                    role="banner"
                >
                    <LoginLayoutHero
                        className=""
                        partnerLogo={
                            showAtlasLogo ? (
                                <img
                                    src="/static/logo-atlas.webp"
                                    alt={t("page.login.hero.atlasLaureate")}
                                    width={256}
                                    height={150}
                                    className="mt-ant-md h-auto w-[120px] self-center xl:w-[160px]"
                                />
                            ) : null
                        }
                        brandLogo={
                            brand?.logo ? (
                                <div className="flex-col items-center justify-center gap-ant-md text-5xl">
                                    <div className="flex flex-1 items-center justify-end">
                                        <div className="w-full self-center rounded-ant-lg p-[1.2rem]">
                                            <img
                                                src={brand.logo}
                                                alt={brand.title}
                                                className="size-full object-contain"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex w-full justify-center">
                                    <img
                                        src="/static/logo-dark-vertical.png"
                                        alt="Simplonline"
                                        className="max-w-[260px] object-contain"
                                    />
                                </div>
                            )
                        }
                    />
                </div>

                <section className="w-full shrink-0 justify-center sm:flex sm:w-ant-screen-xs-max sm:items-center sm:pb-ant xl:min-h-[100cqh] xl:py-ant xl:pr-ant ">
                    <div className="flex w-full flex-col gap-ant-sm bg-ant-container p-ant-xl sm:rounded-ant-lg sm:border sm:border-ant-color">
                        {title ? (
                            <Typography.Title className="m-0!" level={1}>
                                {t(title)}
                            </Typography.Title>
                        ) : null}

                        <div className="flex flex-col">{children}</div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default LoginLayout;
